// The arcade page's own formatters, and the two clock conversions every
// arcade countdown needs.
//
// A match keeps two clocks, exactly as core does (CLAUDE.md, numbers.md
// §19). The wire hands the client game-seconds and says which clock they
// were measured on, and the player watches a wall clock. Every conversion
// is here, in one place, so no panel invents its own.
//
// Nothing in this module touches the DOM or the network, so all of it is
// unit-testable.

import { arcadeFormat as S } from '../gen/strings';
import { groupThousands } from '../locale/group';
import { isoDateTime } from '../locale/stamp';
import { channels, packHex } from '../render/color';
import { SEC_PER_DAY, SEC_PER_HOUR, hhmmss, mmss, wholeSec } from '../locale/clock';

/**
 * The length of one finished match in WALL seconds of fighting, or 0 where
 * the row states none (R1116).
 *
 * `elapsedSec` is the figure: it leaves out the loading panel and the
 * countdown, which the two stamps hold. A row stored before R1112 holds
 * that figure on the map clock, so it is longer than the whole span of
 * the stamps. Such a row falls back to the span.
 */
export function fightLengthSec(elapsedSec: number, startedAt: number, endedAt: number): number {
  const span = startedAt > 0 && endedAt > startedAt ? endedAt - startedAt : 0;
  if (elapsedSec > 0 && (span === 0 || elapsedSec <= span)) return elapsedSec;
  return span;
}

/**
 * The date of one match, as the history row and the match card print it
 * (R1368): the reader's LOCAL date and time, `YYYY-MM-DD HH:MM`, through
 * the client's one stamp formatter (R1544). A row with no stamp prints ''
 * rather than the epoch.
 */
export function matchDate(startedAt: number): string {
  return isoDateTime(startedAt);
}

/** A countdown, as `M:SS`. Seconds round up, so a bar that still shows
 *  `0:01` has not finished yet. */
export function clock(sec: number): string {
  return mmss(Math.ceil(sec));
}

/**
 * A short countdown, as one decimal of a second: `14.3`.
 *
 * The hold countdown over the map is the only clock a match reads to a
 * tenth (R405). It is a last-seconds figure, so it truncates rather than
 * rounds up the way `clock` does: a hold that shows `0.0` has run out, and
 * a hold that has run out is a win the server has already called.
 */
export function tenths(sec: number): string {
  const s = Math.max(0, Number.isFinite(sec) ? sec : 0);
  return (Math.floor(s * 10) / 10).toFixed(1);
}

/** A long countdown, as `HH:MM:SS`. The daily run window is the only one
 *  that needs hours. It is `hhmmss` of `locale/clock.ts` on whole seconds
 *  that are not negative; a span of a day or more keeps its hour count,
 *  which the clock face would take modulo a day. */
export function hms(sec: number): string {
  const s = wholeSec(sec);
  return s < SEC_PER_DAY ? hhmmss(s) : `${Math.floor(s / SEC_PER_HOUR)}:${hhmmss(s).slice(3)}`;
}

/** A whole number with thousands separators. Credits are the only figure
 *  on the arcade page big enough to need one. The mark is the thin space
 *  of `locale/group.ts` and never a machine's own (R687). */
export function credits(n: number): string {
  return groupThousands(Math.max(0, Math.floor(Number.isFinite(n) ? n : 0)));
}

/**
 * Map-clock game-seconds as the wall seconds a player counts.
 *
 * The match timer and the seconds fought are measured on the **map clock**
 * (arcade.md §7.1), which runs at the map's `tacticalscale`. A client that
 * printed `timeout_sec` raw would promise four hours of The Shelf and end
 * the match after eight minutes. `Hello` carries the scale for exactly
 * this conversion.
 *
 * The loading grace and the pre-start countdown do **not** come here. They
 * are real-time deadlines (R267) and the server already sends them in wall
 * seconds; `phase.phaseIsRealTime` says which figure is which.
 *
 * A scale that is not a positive number falls back to 1, which prints the
 * figure the server sent rather than nothing.
 */
export function wallSec(mapSec: number, tacticalScale: number): number {
  const scale = Number.isFinite(tacticalScale) && tacticalScale > 0 ? tacticalScale : 1;
  const v = Number.isFinite(mapSec) ? mapSec : 0;
  return Math.max(0, v) / scale;
}

/**
 * The credit trickle per **wall** second.
 *
 * `credits_per_sec` is paid on the world clock (arcade.md §4.3), so a
 * world running at `timescale` pays that many times faster against a
 * wristwatch. This is the figure the top bar prints beside the bank.
 */
export function trickle(creditsPerSec: number, timescale: number): number {
  const scale = Number.isFinite(timescale) && timescale > 0 ? timescale : 1;
  const rate = Number.isFinite(creditsPerSec) && creditsPerSec > 0 ? creditsPerSec : 0;
  return rate * scale;
}

/** The trickle as the top bar writes it: `+2.50/s`. */
export function trickleText(perWallSec: number): string {
  return `+${(Number.isFinite(perWallSec) ? Math.max(0, perWallSec) : 0).toFixed(2)}/s`;
}

/**
 * Why an unaffordable build row is unaffordable.
 *
 * Round fourteen's rule, which the decided mockup keeps: the row fades its
 * own content and never its reason, so the reason has to say something
 * worth reading at full strength. It names the shortfall and how long the
 * trickle needs to close it.
 *
 * A map that pays nothing per second cannot close any gap, so the wait is
 * omitted rather than printed as an infinity.
 */
export function shortfall(price: number, bank: number, perWallSec: number): string {
  const missing = Math.max(0, Math.ceil(price - bank));
  if (missing <= 0) return '';
  if (!(perWallSec > 0)) return S.shortfall({ missing: credits(missing) });
  return S.shortfall_wait({
    missing: credits(missing), wait: clock(missing / perWallSec),
  });
}

/**
 * How long until a match in this category is likely to start, in wall
 * seconds, or null when the figure cannot be honest.
 *
 * Two server facts make it (R266): a queue that already has somebody in it
 * carries a running fill timer, and `fill_left_sec` is literally the
 * seconds until bots take the empty seats and the match locks. A queue
 * with nobody in it has no timer yet, and joining starts one, so the
 * estimate is the category's configured `fill_after_sec`.
 *
 * This is arithmetic on two numbers the server shipped, not a rule the
 * client invented.
 */
export function startInSec(depth: number, fillLeftSec: number, fillAfterSec: number): number | null {
  if (depth > 0) {
    return Number.isFinite(fillLeftSec) && fillLeftSec >= 0 ? fillLeftSec : null;
  }
  return Number.isFinite(fillAfterSec) && fillAfterSec > 0 ? fillAfterSec : null;
}

/** A seat range, as the board's SEATS column prints it. */
export function seatRange(min: number, max: number): string {
  return min === max ? String(max) : `${min}–${max}`;
}

/**
 * A `#rrggbb` as the three space-separated channels a token takes (R394).
 *
 * The sheet writes a category tint as `rgb(var(--cat-rgb) / .07)`, which is
 * the one form CLAUDE.md allows, so the row has to hand it channels and not
 * a hex. The board sets the variable on the row and the JOIN button inside
 * it reads the same one, so a colour reaches the whole row from one place.
 *
 * Anything that is not a six-digit hex answers "", and the caller then
 * writes no variable at all and keeps the accent the sheet falls back to.
 */
export function rgbChannels(color: string): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return '';
  return channels(packHex(color)).join(' ');
}

/** The style attribute that hands an element its category colour as
 *  `--cat-rgb` (R394, R2070), or '' where `rgbChannels` refuses the
 *  colour, so the element keeps the accent the sheet falls back to. */
export function catStyle(color: string): string {
  const rgb = rgbChannels(color);
  return rgb !== '' ? ` style="--cat-rgb:${rgb}"` : '';
}
